import { CircularProgress, Divider, Grid, Menu } from '@mui/material';
import { HSpacer, RoutalAvatar, RoutalPalette, RoutalTooltip } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import GearIcon from '../../../assets/svg/GearIcon';
import HelpIcon from '../../../assets/svg/HelpIcon';
import LogOutIcon from '../../../assets/svg/LogOutIcon';
import NorthStarIcon from '../../../assets/svg/NorthStarIcon';
import * as routing from '../../../constants/Routing';
import { UpdateSubscriptionDialog } from '../../../dialogs/UpdateSubscriptionDialog';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveFlatProjects } from '../../../hooks/project/useRetrieveFlatProjects';
import { useAsUser } from '../../../hooks/session/useAsUser';
import useDeleteSession from '../../../hooks/session/useDeleteSession';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import Text from '../../../react-components/Text';
import { getUploadAsBase64 } from '../../../services/requests';
import SideBarMenuItemContainer from './SideBarMenuItemContainer';

const CustomMenu = styled(Menu)`
  & .MuiMenu-paper {
    border-radius: 10px;
    box-shadow: 0px 0px 8px 4px rgba(125, 154, 150, 0.15);
  }
`;

const TrialingContainer = styled.div`
  height: 50px;
  padding: 10px 12px;
  background-color: ${RoutalPalette.upgradeColors.medium};
  display: flex;
  align-content: center;
  justify: center;
  border-radius: 10px 10px 0px 0px;
`;

const DaysLeftContainer = styled.div`
  display: flex;
  justify-items: center;
  padding: 0 4px 0 0px;
`;

const DaysLeftTextContainer = styled.div`
  display: flex;
  justify-items: center;
  padding: 8px 0px 8px 4px;
  width: 210px;
`;

const UserSummaryContainer = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -8px;
`;

const UserSummary = styled.div`
  margin-top: auto;
  padding: 14px 12px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
`;

const NameContainer = styled.div`
  text-align: left;
`;

const CustomMenuItem = styled.div`
  width: 100%;
  box-sizing: border-box;
  min-width: 256px;
  height: 48px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

export const SideBarUser = () => {
  const { run: logout } = useDeleteSession();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: user, run: fetchUser } = useRetrieveUser();
  const { asUser, token } = useAsUser();
  const { run: refetchProjects } = useRetrieveFlatProjects({ user });
  const { data: organization, run: refetchOrganization } = useRetrieveOrganization();
  const navigate = useNavigate();
  const [inspectedUser, setInspectedUser] = useState<any>(undefined);
  const [userAnchorEl, setUserAnchorEl] = useState<(EventTarget & HTMLDivElement) | null>(null);
  const [changePlanOpen, setChangePlanOpen] = useState<boolean>(false);

  const handleUserItemAction = (action: any) => {
    setUserAnchorEl(null);
    action.onClick && action.onClick();
  };

  useEffect(() => {
    if ((!user && token) || asUser !== inspectedUser) {
      setInspectedUser(asUser);
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asUser, user, token, inspectedUser, setInspectedUser]);

  const handleLogOut = () => {
    logout();
  };

  const showTrialingContainer =
    organization?.subscription.status === `trialing` || organization?.subscription.status === `canceled`;

  const daysLeft = (time: string) => Math.floor((Date.parse(`${time}`) - +new Date()) / (3600 * 24 * 1000));

  const userActions = [
    ...(user
      ? [
          {
            id: `profilesumary`,
            label: (
              <UserSummaryContainer style={{ borderRadius: showTrialingContainer ? undefined : `10px 10px 0px 0px` }}>
                {showTrialingContainer && (
                  <TrialingContainer
                    onClick={() => {
                      navigate(routing.SETTINGS.BILLING.SUBSCRIPTION);
                    }}
                  >
                    {daysLeft(`${organization.subscription.current_period_end}`) > 0 ? (
                      <>
                        <DaysLeftContainer>
                          <Grid
                            style={{
                              position: `relative`,
                              display: `inline-flex`,
                              alignItems: `center`,
                            }}
                          >
                            <CircularProgress
                              variant="determinate"
                              value={daysLeft(`${organization.subscription.current_period_end}`) * 10}
                              size={28}
                              thickness={2}
                              style={{
                                color: RoutalPalette.neutral02n,
                              }}
                            />
                            <Grid
                              style={{
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                                position: `absolute`,
                                display: `flex`,
                                alignItems: `center`,
                                justifyContent: `center`,
                              }}
                            >
                              <Text style={{ fontWeight: 400, color: RoutalPalette.neutral02n }}>
                                {daysLeft(`${organization.subscription.current_period_end}`)}
                              </Text>
                            </Grid>
                          </Grid>
                        </DaysLeftContainer>
                        <DaysLeftTextContainer>
                          <Text intlId="user_profile_summary.daysleft" style={{ color: RoutalPalette.neutral02n }} />
                        </DaysLeftTextContainer>
                      </>
                    ) : (
                      <Grid container justifyContent="center" alignItems="center">
                        <Text
                          style={{ width: `80%`, textAlign: `center` }}
                          intlId="user_profile_summary.nosubscription"
                        />
                      </Grid>
                    )}
                  </TrialingContainer>
                )}
                <UserSummary>
                  <RoutalAvatar
                    url={user.avatar_url}
                    getUploadAsBase64={getUploadAsBase64}
                    label={`${user.first_name} ${user.last_name}`}
                  />

                  <HSpacer small />

                  <NameContainer>
                    <Text variant="body" style={{ fontWeight: `600` }}>
                      {user.first_name} {user.last_name}
                    </Text>
                    <div style={{ minHeight: `4px` }} />
                    <Text variant="small">{organization?.label ?? ``}</Text>
                  </NameContainer>
                </UserSummary>
              </UserSummaryContainer>
            ),
          },
          {
            isDivider: true,
          },
          ...(userCredentials?.test(`can_manage_backoffice`) ||
          asUser ||
          userCredentials?.test(`can_manage_translations`)
            ? [
                {
                  id: `backoffice`,
                  label: (
                    <CustomMenuItem>
                      <NorthStarIcon color={RoutalPalette.secondary60} />
                      <div style={{ minWidth: `5px` }} />
                      <Text style={{ cursor: `pointer` }} intlId="user_profile_summary.backoffice" />
                    </CustomMenuItem>
                  ),
                  onClick: () => {
                    navigate(routing.BACKOFFICE.ROOT);
                  },
                },
                ...(organization && !asUser
                  ? [
                      {
                        id: `change_plan`,
                        label: (
                          <CustomMenuItem>
                            <NorthStarIcon color={RoutalPalette.secondary60} />
                            <div style={{ minWidth: `5px` }} />
                            <Text style={{ cursor: `pointer` }} intlId="user_profile_summary.change_plan" />
                          </CustomMenuItem>
                        ),
                        onClick: () => {
                          setChangePlanOpen(true);
                        },
                      },
                    ]
                  : []),
              ]
            : []),
          ...(userCredentials?.test(`can_manage_backoffice`) ||
          asUser ||
          userCredentials?.test(`can_manage_translations`)
            ? [
                {
                  isDivider: true,
                },
              ]
            : []),
          {
            id: `settings`,
            label: (
              <CustomMenuItem>
                <GearIcon />
                <div style={{ minWidth: `4px` }} />
                <Text style={{ cursor: `pointer` }} intlId="user_profile_summary.settings" />
              </CustomMenuItem>
            ),
            onClick: () => {
              navigate(routing.SETTINGS.ACCOUNT.PROFILE);
            },
          },
          {
            id: `help`,
            label: (
              <CustomMenuItem>
                <HelpIcon />
                <div style={{ minWidth: `4px` }} />
                <Text style={{ cursor: `pointer` }} intlId="user_profile_summary.help" />
              </CustomMenuItem>
            ),
            onClick: () => window.open(routing.EXTERNAL.DOCS),
          },
          {
            isDivider: true,
          },
          {
            id: `logout`,
            label: (
              <CustomMenuItem>
                <LogOutIcon color={RoutalPalette.error.medium} />
                <div style={{ minWidth: `4px` }} />
                <Text
                  style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                  intlId="user_profile_summary.logout"
                />
              </CustomMenuItem>
            ),
            onClick: handleLogOut,
          },
        ]
      : []),
  ];

  return (
    <>
      <RoutalTooltip id="user_settings" title={`${user?.first_name} ${user?.last_name}`} placement="right">
        <SideBarMenuItemContainer onClick={(e) => setUserAnchorEl(e.currentTarget)}>
          {user && (
            <RoutalAvatar
              aria-owns={userAnchorEl ? `user-actions` : undefined}
              aria-haspopup="true"
              url={user.avatar_url}
              getUploadAsBase64={getUploadAsBase64}
              label={`${user?.first_name}_${user?.last_name}`}
              containerStyles={{ width: `28px`, height: `28px` }}
            />
          )}
        </SideBarMenuItemContainer>
      </RoutalTooltip>

      {user && userActions !== undefined && userActions.length !== 0 && (
        <CustomMenu
          id="user-actions"
          anchorEl={userAnchorEl}
          open={Boolean(userAnchorEl)}
          onClose={(_e) => setUserAnchorEl(null)}
          anchorOrigin={{ horizontal: `right`, vertical: `bottom` }}
        >
          {userActions.map((action, i) =>
            action.isDivider ? (
              <Divider key={`divider-${i}`} />
            ) : (
              <div key={action.id} onClick={() => handleUserItemAction(action)}>
                {action.label}
              </div>
            )
          )}
        </CustomMenu>
      )}

      {user && organization && changePlanOpen ? (
        <UpdateSubscriptionDialog
          userId={user.id}
          subscriptionData={{
            plan: organization.subscription.plan,
            extra_vehicles: organization.subscription.vehicles,
            extra_supervisors: organization.subscription.supervisors,
            extra_projects: organization.subscription.projects,
            extra_users: organization.subscription.users,
            current_period_end: organization.subscription.current_period_end,
          }}
          onClose={(modified?: boolean) => {
            if (modified) {
              refetchProjects();
              refetchOrganization();
            }
            setUserAnchorEl(null);
            setChangePlanOpen(false);
          }}
        />
      ) : null}
    </>
  );
};
